import { Provider, useDispatch, useSelector } from 'react-redux';
import './App.css';
import AuthExpirationWatcher from './components/AuthExpirationWatcher/AuthExpirationWatcher';
import LandingPage from './components/LandingPage/LandingPage';
import Registration from './components/Registration/Registration';
import NavBar from './components/NavBar/NavBar';
import Footer from './components/Footer/Footer';
import VerifyEmailAction from './components/EmailVerification/VerifyemailAction';
import Login from './components/LoginPage/Login';
import ProfilePage from './components/ProfilePage/ProfilePage';
import ForgotPassword from './components/ForgotPassword/EmailEntry/ForgotPassword';
import EmailSent from './components/ForgotPassword/EmailSent/EmailSent';
import ResetPassword from './components/ForgotPassword/ResetPassword/ResetPassword';
import EmailEntry from './components/ResendEmail/EmailEntry/EmailEntry';
import store from './store';
import { useEffect } from 'react';
import { fetchUser, logout, logoutUser, refreshToken } from './slices/userSlice';
import { Navigate, useLocation, BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          font-family: 'Poppins', sans-serif;
        }
      `,
    },
  },
});

function AppContent() {
  const dispatch = useDispatch();
  const location = useLocation();

  const { isAuthenticated, authChecked } = useSelector((state) => state.user);

  const currentPaths = location.pathname.toLowerCase();
  const publicPaths = [
    '/login',
    '/registration',
    '/reset-password',
    '/verify',
    '/check-inbox',
    '/email-verification',
    '/resend-verification'
  ];

  const isPublicPath = publicPaths.some(path =>
    currentPaths.startsWith(path)
  );
  useEffect(() => {
    const initAuth = async () => {
      try {
        const refreshResult = await dispatch(refreshToken()).unwrap();

        if (refreshResult?.token) {
          await dispatch(fetchUser()).unwrap();
        }
      } catch (err) {
        dispatch(logoutUser());
      }
    };

    initAuth();

    const handleStorageChange = (event) => {
      if (event.key === "logout-event") {
        localStorage.removeItem("logout-event");
        dispatch(logout());
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    }
  }, [dispatch]);

  if (!authChecked) {
    return (
      <div className="loading-screen">
        Verifying Session...
      </div>
    );
  }

  if (!isAuthenticated && !isPublicPath) {
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  return (
    <>
      <AuthExpirationWatcher />
      <NavBar />
      <AuthExpirationWatcher />
      <ToastContainer
        className="toast-container"
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />

      <div className="App" data-testid="App">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/registration" element={<Registration />} />
          <Route path="/login" element={<Login timerKey="loginPageResendExpiry" />} />
          <Route path="/ProfilePage" element={<ProfilePage />} />
          <Route path="/email-verification" element={<ForgotPassword timerKey="passwordResetResendExpiry" />} />
          <Route path="/resend-verification" element={<EmailEntry timerKey="emailVerificationResendExpiry" />} />
          <Route path="/check-inbox" element={<EmailSent />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify" element={<VerifyEmailAction />} />
          <Route path="/tickets/:ticketId" element={<LandingPage />} />
        </Routes>
      </div>

      <Footer />
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </Provider>
  );
}

export default App;